import { useState, useRef, useContext, useEffect } from "react";
import { useSwipeable } from "react-swipeable";

import SuccessModal from "./SuccessModal";
import CreateSaleSheet from "./CreateSaleSheet";
import SaleSheetContext from "./SaleSheetContext";

import "./SaleSheetSlide.css";
import {
    Slide,
    Container,
    Box,
    Button,
    Flex,
    Heading,
    SimpleGrid,
    GridItem,
    Input,
    InputGroup,
    InputRightElement,
    useDisclosure,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Select,
    Spacer,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";

const headerZIndex = 1000;
const emailRegExp = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
const threshold = window.screen.height / 2; // Threshold for closing the modal
const initialStyle = { zIndex: headerZIndex + 2 };

function MaskOverlay({ onClose }) {
    return (
        <Flex
            w="100%"
            h="100%"
            position="fixed"
            top="0px"
            left={0}
            backgroundColor="#00000070"
            onClick={onClose}
            zIndex={headerZIndex + 1}
        />
    );
}

function SwipeableBar() {
    return (
        <Box
            w="143px"
            h="6px"
            mx="auto"
            mt="12px"
            mb="12px"
            backgroundColor="#D9D9D9"
            borderRadius="24px"
        />
    );
}

export default function SaleSheetSlide({
    isOpen: isSlideOpen,
    onClose: onSlideClose,
    modelName,
    countryList,
}) {
    const {
        isOpen: isModalOpen,
        onOpen: onModalOpen,
        onClose: onModalClose,
    } = useDisclosure();

    const ctx = useContext(SaleSheetContext);
    const [draggedStyle, setDraggedStyle] = useState(initialStyle);
    const [fileName, setFileName] = useState();
    const imgRef = useRef(null);

    let slidePosY = 0;
    const swipeable = useSwipeable({
        onSwipedDown: (e) => {
            if (e.deltaY >= threshold) {
                onSwipeDown();
            } else {
                bounceBack();
            }
        },
        onSwiping: (e) => {
            if (e.deltaY > 0) {
                onDragDown(e);
            }
        },
    });

    const onDragDown = (e) => {
        slidePosY = -e.deltaY;
        setDraggedStyle({
            ...initialStyle,
            // can't overwrite transform to slide, use bottom instead
            // transform: transform,
            bottom: slidePosY,
            backfaceVisibility: "hidden",
        });
    };

    const bounceBack = () => {
        const duration = 300;
        setDraggedStyle({
            ...initialStyle,
            // can't overwrite transform to slide, use bottom instead
            // transform: "translateY(0px)",
            // transition: `transform ${duration}ms`,
            backfaceVisibility: "hidden",
        });
        setTimeout(() => {
            setDraggedStyle({ ...initialStyle });
        }, duration);
    };

    const onSwipeDown = () => {
        const duration = 30;
        setDraggedStyle({
            ...initialStyle,
            // can't overwrite transform to slide, use bottom instead
            // transform: transform,
            // transition: `transform ${duration}ms`,
            bottom: -window.screen.height,
            backfaceVisibility: "hidden",
        });

        setTimeout(() => {
            setDraggedStyle({ ...initialStyle });
            onSlideClose(); // Trigger click on the hidden button
        }, duration);
    };

    const isImgInvalid = !fileName;
    const isEmailInvalid = !ctx.saleSheetData.email || !emailRegExp.test(ctx.saleSheetData.email);

    function handleCreateSaleSheet(event) {
        event.preventDefault();
        if (isImgInvalid || isEmailInvalid) return;

        const img = imgRef.current.files[0];
        const product = localStorage.getItem("product");
        const model = localStorage.getItem("product");
        const modelName = localStorage.getItem("model_name");
        const type = localStorage.getItem("type");

        CreateSaleSheet(
            img,
            ctx.saleSheetData.customerName,
            ctx.saleSheetData.email,
            ctx.saleSheetData.country,
            product,
            model,
            modelName,
            type,
            onModalOpen
        );
    }

    //prevent body drag event
    document.body.style.overflowY = isSlideOpen ? "hidden" : "scroll";

    return (
        <>
            <SuccessModal
                isOpen={isModalOpen}
                onClose={() => {
                    onModalClose();
                    onSlideClose();
                }}
            />

            {isSlideOpen && <MaskOverlay onClose={onSlideClose} />}
            <Slide
                direction="bottom"
                in={isSlideOpen}
                unmountOnExit="true"
                style={draggedStyle}
                {...swipeable}
                onClick={onSlideClose}
            >
                <Container
                    w="100vw"
                    maxW="500px"
                    borderRadius="25px 25px 0px 0px"
                    p="2px 20px 26px 20px"
                    direction="column"
                    backgroundColor="white"
                    onClick={(e) => e.stopPropagation()}
                >
                    <SwipeableBar />
                    <Heading
                        as="h4"
                        fontSize="18px"
                        fontWeight={600}
                        mx="auto"
                        mt="6px"
                        mb="22px"
                        color="black"
                        textAlign="center"
                    >
                        Custom Salesheet
                    </Heading>
                    <SimpleGrid
                        h="560px"
                        maxH="70vh"
                        columns={1}
                        rows={5}
                        rowGap="8px"
                        alignItems="flex-start"
                    >
                        <GridItem rowSpan={1}>
                            <FormControl isInvalid={isImgInvalid} isRequired>
                                <FormLabel
                                    color="#495057"
                                    fontSize="12px"
                                    fontWeight="400"
                                >
                                    Product photo
                                </FormLabel>
                                <Input
                                    type="file"
                                    onChange={(e) => {
                                        setFileName(e.target.value);
                                    }}
                                    ref={imgRef}
                                />
                            </FormControl>
                        </GridItem>
                        <GridItem rowSpan={1}>
                            <FormControl>
                                <FormLabel
                                    color="#495057"
                                    fontSize="12px"
                                    fontWeight="400"
                                >
                                    Full name
                                </FormLabel>
                                <Input
                                    placeholder="Ben Smith"
                                    value={ctx.saleSheetData.customerName}
                                    onChange={(e) => {
                                        ctx.setCustomerName(e.target.value);
                                    }}
                                ></Input>
                            </FormControl>
                        </GridItem>
                        <GridItem rowSpan={1}>
                            <FormControl isInvalid={isEmailInvalid} isRequired>
                                <Flex
                                    mb="8px"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    alignSelf="stretch"
                                >
                                    <FormLabel
                                        m={0}
                                        fontSize="12px"
                                        fontWeight="400"
                                    >
                                        Email
                                    </FormLabel>
                                    <Spacer />
                                    <FormErrorMessage m="auto">
                                        Please enter valid email
                                    </FormErrorMessage>
                                </Flex>
                                <InputGroup>
                                    <Input
                                        value={ctx.saleSheetData.email}
                                        placeholder="Bensmith@greatclient.com"
                                        onChange={(e) => {
                                            ctx.setEmail(e.target.value);
                                        }}
                                    />
                                    <InputRightElement>
                                        {isEmailInvalid && (
                                            <WarningTwoIcon color="#F03D3E" />
                                        )}
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>
                        </GridItem>
                        <GridItem rowSpan={1}>
                            <FormControl>
                                <FormLabel
                                    color="#495057"
                                    fontSize="12px"
                                    fontWeight="400"
                                >
                                    Country
                                </FormLabel>
                                <Select
                                    placeholder="Select from list"
                                    value={ctx.saleSheetData.country}
                                    onChange={(e) => {
                                        ctx.setCountry(e.target.value);
                                    }}
                                >
                                    {(Array.isArray(countryList) ? countryList : []).map((country) => {
                                        return (
                                            <option
                                                key={country.value}
                                                value={country.value}
                                            >
                                                {country.label}
                                            </option>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </GridItem>
                        <GridItem rowSpan={1}>
                            <FormControl>
                                <FormLabel
                                    color="#495057"
                                    fontSize="12px"
                                    fontWeight="400"
                                >
                                    Model Name
                                </FormLabel>
                                <Input
                                    value={modelName}
                                    isReadOnly={true}
                                ></Input>
                            </FormControl>
                        </GridItem>
                        <GridItem rowSpan={0} pt="11px" justifySelf="center">
                            <Button
                                w="255px"
                                h="48px"
                                borderRadius="12px"
                                backgroundColor="#DB0025"
                                fontSize="12px"
                                textColor="white"
                                textTransform="uppercase"
                                letterSpacing="1px"
                                onClick={handleCreateSaleSheet}
                            >
                                Create Sale Sheet
                            </Button>
                        </GridItem>
                    </SimpleGrid>
                </Container>
            </Slide>
        </>
    );
}
